import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Building, 
  Users, 
  Mail, 
  Calendar, 
  CreditCard, 
  CheckCircle, 
  AlertTriangle,
  LogOut,
  BarChart3,
  Loader2,
  Shield,
  Eye,
  EyeOff,
  Trash2,
  Sparkles,
  FileText,
  Receipt,
  Building2,
  Pencil,
  ArrowLeftRight
} from "lucide-react";
import { RicevutaDialog, RicevuteList } from "@/components/SuperAdminRicevute";
import { SuperAdminRecensioni } from "@/components/SuperAdminRecensioni";
import { RicevuteDaEmettere, useRicevuteMese } from "@/components/RicevuteDaEmettere";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  const [viewTestimonials, setViewTestimonials] = useState(null);
  const [viewBilling, setViewBilling] = useState(null);
  const [ricevutaFor, setRicevutaFor] = useState(null);
  const toggleEmailExtra = async (company) => {
    const enable = !company.email_extra_recipients_enabled;
    if (!window.confirm(`${enable ? "Attivare" : "Disattivare"} i destinatari email aggiuntivi per ${company.nome_azienda}?`)) return;
    try {
      await axios.post(`${API}/api/super-admin/toggle-email-extra`, { azienda_id: company.id, enabled: enable }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Email aggiuntive ${enable ? "attivate" : "disattivate"} per ${company.nome_azienda}`);
      setCompanies((prev) => prev.map((c) => (c.id === company.id ? { ...c, email_extra_recipients_enabled: enable } : c)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const toggleModifica = async (company) => {
    const enable = !company.modifica_approvate_enabled;
    if (!window.confirm(`${enable ? "Attivare" : "Disattivare"} la matita "modifica richieste approvate" (solo titolare) per ${company.nome_azienda}?`)) return;
    try {
      await axios.post(`${API}/api/super-admin/toggle-modifica-approvate`, { azienda_id: company.id, enabled: enable }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Modifica approvate ${enable ? "attivata" : "disattivata"} per ${company.nome_azienda}`);
      setCompanies((prev) => prev.map((c) => (c.id === company.id ? { ...c, modifica_approvate_enabled: enable } : c)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const toggleMulti = async (company) => {
    const enable = !company.multi_azienda_enabled;
    if (!window.confirm(`${enable ? "Attivare" : "Disattivare"} "più aziende / Passa a" per ${company.nome_azienda}?`)) return;
    try {
      await axios.post(`${API}/api/super-admin/toggle-multi-azienda`, { azienda_id: company.id, enabled: enable }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Più aziende ${enable ? "attivato" : "disattivato"} per ${company.nome_azienda}`);
      setCompanies((prev) => prev.map((c) => (c.id === company.id ? { ...c, multi_azienda_enabled: enable } : c)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const toggleReparti = async (company) => {
    const enable = !company.reparti_enabled;
    if (!window.confirm(`${enable ? "Attivare" : "Disattivare"} la funzione Reparti per ${company.nome_azienda}?`)) return;
    try {
      await axios.post(`${API}/api/super-admin/toggle-reparti`, { azienda_id: company.id, enabled: enable }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Reparti ${enable ? "attivati" : "disattivati"} per ${company.nome_azienda}`);
      setCompanies((prev) => prev.map((c) => (c.id === company.id ? { ...c, reparti_enabled: enable } : c)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };
  const [ricevuteKey, setRicevuteKey] = useState(0);
  const [token, setToken] = useState(null);
  const ricevuteStato = useRicevuteMese(token, ricevuteKey);
  const ricevutaMancante = (c) => !!(c.payment_completed && ricevuteStato && !ricevuteStato.emesse.has(c.id) && (ricevuteStato.incassi[c.id]?.pagamenti || []).length > 0);

  // Check for secret key in URL or existing token
  useEffect(() => {
    const secretKey = searchParams.get("key");
    const storedToken = localStorage.getItem("super_admin_token");
    
    if (secretKey) {
      setToken(secretKey);
      setIsAuthenticated(true);
      setLoading(false);
    } else if (storedToken) {
      setToken(storedToken);
      setIsAuthenticated(true);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchData();
    }
  }, [isAuthenticated, token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [companiesRes, statsRes, testiRes] = await Promise.all([
        axios.get(`${API}/api/super-admin/companies`, { headers }),
        axios.get(`${API}/api/super-admin/stats`, { headers }),
        axios.get(`${API}/api/super-admin/testimonials`, { headers }).catch(() => ({ data: { testimonials: [] } }))
      ]);
      
      setCompanies(companiesRes.data.companies);
      setStats(statsRes.data);
      setTestimonials(testiRes.data.testimonials || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
        toast.error("Sessione scaduta, effettua nuovamente l'accesso");
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/super-admin/login`, {
        email,
        password
      });
      
      const newToken = response.data.token;
      localStorage.setItem("super_admin_token", newToken);
      setToken(newToken);
      setIsAuthenticated(true);
      toast.success("Accesso effettuato!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Credenziali non valide");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("super_admin_token");
    setToken(null);
    setIsAuthenticated(false);
    setCompanies([]);
    setStats(null);
    navigate("/super-admin");
  };

  const getStatusBadge = (status, paymentCompleted) => {
    if (paymentCompleted) {
      return <Badge className="bg-emerald-100 text-emerald-800">Attivo</Badge>;
    }
    
    switch (status) {
      case "trial":
        return <Badge className="bg-blue-100 text-blue-800">Trial</Badge>;
      case "expired":
        return <Badge className="bg-red-100 text-red-800">Scaduto</Badge>;
      case "blocked":
        return <Badge className="bg-red-100 text-red-800">Bloccato</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-800">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Login form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <Shield className="h-6 w-6 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl">Super-Admin</CardTitle>
            <CardDescription>Accedi alla dashboard di amministrazione</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@ferie-e-permessi.it"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={loginLoading}>
                {loginLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Accesso...
                  </>
                ) : (
                  "Accedi"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleDeleteCompany = async (company) => {
    if (!window.confirm(`Eliminare DEFINITIVAMENTE l'azienda "${company.nome_azienda}" con tutti i suoi utenti e dati?`)) return;
    if (!window.confirm(`Sei proprio sicuro? L'operazione NON è reversibile.`)) return;
    try {
      const canForce = company.payment_completed && !company.num_dipendenti && !company.has_stripe_subscription;
      await axios.delete(`${API}/api/super-admin/companies/${company.id}${canForce ? "?force=true" : ""}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Azienda "${company.nome_azienda}" eliminata`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const testimonialsByCompany = testimonials.reduce((acc, t) => {
    if (t.azienda_id) (acc[t.azienda_id] = acc[t.azienda_id] || []).push(t);
    return acc;
  }, {});

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir(["data_registrazione", "trial_end_date", "fattura", "ricevuta", "reparti_enabled", "email_extra_recipients_enabled", "modifica_approvate_enabled", "multi_azienda_enabled"].includes(field) ? "desc" : "asc");
    }
  };

  const sortedCompanies = [...companies].sort((a, b) => {
    if (!sortField) return 0;
    let va, vb;
    if (sortField === "testimonianza") {
      va = (testimonialsByCompany[a.id] || []).length > 0 ? 1 : 0;
      vb = (testimonialsByCompany[b.id] || []).length > 0 ? 1 : 0;
    } else if (sortField === "fattura") {
      va = a.billing_info?.denominazione ? 1 : 0;
      vb = b.billing_info?.denominazione ? 1 : 0;
    } else if (sortField === "ricevuta") {
      va = ricevutaMancante(a) ? 2 : (ricevuteStato?.emesse.has(a.id) ? 1 : 0);
      vb = ricevutaMancante(b) ? 2 : (ricevuteStato?.emesse.has(b.id) ? 1 : 0);
    } else if (["reparti_enabled", "email_extra_recipients_enabled", "modifica_approvate_enabled", "multi_azienda_enabled"].includes(sortField)) {
      va = a[sortField] ? 1 : 0;
      vb = b[sortField] ? 1 : 0;
    } else if (sortField === "num_dipendenti") {
      va = a.num_dipendenti || 0;
      vb = b.num_dipendenti || 0;
    } else if (["data_registrazione", "trial_end_date"].includes(sortField)) {
      va = a[sortField] ? new Date(a[sortField]).getTime() : 0;
      vb = b[sortField] ? new Date(b[sortField]).getTime() : 0;
    } else {
      va = (a[sortField] || "").toString().toLowerCase();
      vb = (b[sortField] || "").toString().toLowerCase();
    }
    if (va < vb) return sortDir === "asc" ? -1 : 1;
    if (va > vb) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const STORE_LABELS = {
    android_app: "Google Play",
    ios_app: "App Store",
    pwa: "Microsoft/PWA",
    web: "Sito web"
  };

  const SortableTh = ({ field, children }) => (
    <th
      className="text-left py-3 px-2 text-sm font-medium text-slate-500 cursor-pointer select-none hover:text-slate-800 whitespace-nowrap"
      onClick={() => handleSort(field)}
      title="Clicca per ordinare (crescente/decrescente)"
      data-testid={`sort-${field}`}
    >
      {children} {sortField === field ? (sortDir === "asc" ? "▲" : "▼") : ""}
    </th>
  );

  // Dashboard
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Shield className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900">Super-Admin Dashboard</h1>
                <p className="text-xs text-slate-500">Ferie e Permessi</p>
              </div>
            </div>
            <Button variant="outline" onClick={handleLogout} className="gap-2">
              <LogOut className="h-4 w-4" />
              Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Aziende Totali</p>
                    <p className="text-3xl font-bold">{stats.total_companies}</p>
                  </div>
                  <Building className="h-8 w-8 text-slate-300" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Dipendenti Totali</p>
                    <p className="text-3xl font-bold">{stats.total_employees}</p>
                  </div>
                  <Users className="h-8 w-8 text-slate-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-600">Paganti</p>
                    <p className="text-3xl font-bold text-emerald-700">{stats.active_companies}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-emerald-300" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200" data-testid="sa-pro-light-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-700">Pro Light attivi</p>
                    <p className="text-3xl font-bold text-amber-700" data-testid="sa-pro-light-count">{stats.pro_light_companies ?? 0}</p>
                    <p className="text-xs text-amber-600 mt-1" data-testid="sa-pro-light-promos">
                      Email promo inviate: <strong>{stats.pro_light_promo_sent ?? 0}</strong>
                    </p>
                  </div>
                  <Sparkles className="h-8 w-8 text-amber-300" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Companies List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              Aziende Registrate
            </CardTitle>
            <CardDescription>
              Lista completa delle aziende registrate sulla piattaforma
            </CardDescription>
          </CardHeader>
          <CardContent>
            {companies.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Building className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>Nessuna azienda registrata</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <SortableTh field="nome_azienda">Azienda</SortableTh>
                      <SortableTh field="titolare_nome">Titolare</SortableTh>
                      <SortableTh field="data_registrazione">Registrazione</SortableTh>
                      <SortableTh field="subscription_status">Stato</SortableTh>
                      <SortableTh field="piano_nome">Piano</SortableTh>
                      <SortableTh field="num_dipendenti">Dipendenti</SortableTh>
                      <SortableTh field="registration_source">Store</SortableTh>
                      <SortableTh field="testimonianza">Testimonianza</SortableTh>
                      <SortableTh field="fattura">Fattura</SortableTh>
                      <SortableTh field="ricevuta">Ricevuta</SortableTh>
                      <SortableTh field="reparti_enabled">Reparti</SortableTh>
                      <SortableTh field="email_extra_recipients_enabled">Email+</SortableTh>
                      <SortableTh field="modifica_approvate_enabled">Matita</SortableTh>
                      <SortableTh field="multi_azienda_enabled">Multi</SortableTh>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCompanies.map((company) => {
                      const companyTestimonials = testimonialsByCompany[company.id] || [];
                      
                      return (
                        <tr key={company.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-4 px-2">
                            <div className="font-medium text-slate-900">{company.nome_azienda}</div>
                            <div className="text-xs text-slate-400">ID: {company.id}</div>
                          </td>
                          <td className="py-4 px-2">
                            <div className="text-sm text-slate-900">{company.titolare_nome}</div>
                            <div className="text-xs text-slate-500 flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {company.titolare_email}
                            </div>
                          </td>
                          <td className="py-4 px-2">
                            <div className="text-sm text-slate-600">
                              {formatDate(company.data_registrazione)}
                            </div>
                          </td>
                          <td className="py-4 px-2">
                            {getStatusBadge(company.subscription_status, company.payment_completed)}
                          </td>
                          <td className="py-4 px-2">
                            <div className="text-sm text-slate-600">{company.piano_nome}</div>
                            {company.piano_nome === "Free" && company.num_dipendenti > 5 && (
                              <div className="text-xs text-red-600" data-testid={`sa-over-limit-${company.id}`}>oltre 5 dip. — non paga</div>
                            )}
                          </td>
                          <td className="py-4 px-2">
                            <div className="flex items-center gap-1 text-sm text-slate-600">
                              <Users className="h-4 w-4" />
                              {company.num_dipendenti}
                            </div>
                          </td>
                          <td className="py-4 px-2">
                            <span className="text-sm text-slate-600 whitespace-nowrap" data-testid={`store-${company.id}`}>
                              {STORE_LABELS[company.registration_source] || "—"}
                            </span>
                          </td>
                          <td className="py-4 px-2">
                            {companyTestimonials.length > 0 ? (
                              <button
                                className="text-sm font-bold text-emerald-600 hover:text-emerald-800 underline"
                                onClick={() => setViewTestimonials({ company, items: companyTestimonials })}
                                data-testid={`testimonial-yes-${company.id}`}
                              >
                                SI
                              </button>
                            ) : (
                              <span className="text-sm text-slate-400">—</span>
                            )}
                          </td>
                          <td className="py-4 px-2">
                            {company.billing_info?.denominazione ? (
                              <button
                                type="button"
                                onClick={() => setViewBilling(company)}
                                className="text-blue-600 hover:text-blue-800"
                                title="Dati per la fattura"
                                data-testid={`billing-view-${company.id}`}
                              >
                                <FileText className="h-5 w-5" />
                              </button>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          <td className="py-4 px-2">
                            <button
                              type="button"
                              onClick={() => setRicevutaFor(company)}
                              className={ricevutaMancante(company) ? "text-amber-500 hover:text-amber-700" : "text-emerald-600 hover:text-emerald-800"}
                              title={ricevutaMancante(company) ? "Pagamento incassato questo mese, ricevuta NON ancora emessa — clicca per emetterla" : "Emetti ricevuta con ritenuta d'acconto"}
                              data-testid={`ricevuta-new-${company.id}`}
                            >
                              <Receipt className="h-5 w-5" />
                            </button>
                          </td>
                          <td className="py-4 px-2">
                            <button
                              type="button"
                              onClick={() => toggleReparti(company)}
                              className={company.reparti_enabled ? "text-blue-600 hover:text-blue-800" : "text-slate-300 hover:text-slate-500"}
                              title={company.reparti_enabled ? "Reparti ATTIVI — clicca per disattivare" : "Reparti disattivati — clicca per attivare"}
                              data-testid={`reparti-toggle-${company.id}`}
                            >
                              <Building2 className="h-5 w-5" />
                            </button>
                          </td>
                          <td className="py-4 px-2">
                            <button
                              type="button"
                              onClick={() => toggleEmailExtra(company)}
                              className={company.email_extra_recipients_enabled ? "text-blue-600 hover:text-blue-800" : "text-slate-300 hover:text-slate-500"}
                              title={company.email_extra_recipients_enabled ? "Destinatari email aggiuntivi ATTIVI — clicca per disattivare" : "Destinatari email aggiuntivi disattivati — clicca per attivare"}
                              data-testid={`email-extra-toggle-${company.id}`}
                            >
                              <Mail className="h-5 w-5" />
                            </button>
                          </td>
                          <td className="py-4 px-2">
                            <button
                              type="button"
                              onClick={() => toggleModifica(company)}
                              className={company.modifica_approvate_enabled ? "text-blue-600 hover:text-blue-800" : "text-slate-300 hover:text-slate-500"}
                              title={company.modifica_approvate_enabled ? "Matita modifica richieste approvate ATTIVA (solo titolare) — clicca per disattivare" : "Matita modifica richieste approvate disattivata — clicca per attivare"}
                              data-testid={`modifica-toggle-${company.id}`}
                            >
                              <Pencil className="h-5 w-5" />
                            </button>
                          </td>
                          <td className="py-4 px-2">
                            <button
                              type="button"
                              onClick={() => toggleMulti(company)}
                              className={company.multi_azienda_enabled ? "text-blue-600 hover:text-blue-800" : "text-slate-300 hover:text-slate-500"}
                              title={company.multi_azienda_enabled ? "Più aziende / Passa a ATTIVO — clicca per disattivare" : "Più aziende / Passa a disattivato — clicca per attivare"}
                              data-testid={`multi-toggle-${company.id}`}
                            >
                              <ArrowLeftRight className="h-5 w-5" />
                            </button>
                          </td>
                          <td className="py-4 px-2">
                            {(!company.payment_completed || (!company.num_dipendenti && !company.has_stripe_subscription)) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                title="Elimina azienda (definitivo)"
                                onClick={() => handleDeleteCompany(company)}
                                data-testid={`delete-company-${company.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialog dati fattura */}
        <Dialog open={!!viewBilling} onOpenChange={(open) => !open && setViewBilling(null)}>
          <DialogContent className="max-w-md" data-testid="billing-dialog">
            <DialogHeader>
              <DialogTitle>Dati per la fattura — {viewBilling?.nome_azienda}</DialogTitle>
              <DialogDescription>Inseriti dal titolare nella pagina Abbonamento</DialogDescription>
            </DialogHeader>
            {viewBilling?.billing_info && (
              <div className="text-sm text-slate-700 space-y-1">
                {[
                  ["Denominazione", viewBilling.billing_info.denominazione],
                  ["Indirizzo", viewBilling.billing_info.indirizzo],
                  ["CAP / Città / Prov.", [viewBilling.billing_info.cap, viewBilling.billing_info.citta, viewBilling.billing_info.provincia].filter(Boolean).join(" ")],
                  ["Partita IVA", viewBilling.billing_info.partita_iva],
                  ["Codice fiscale", viewBilling.billing_info.codice_fiscale],
                  ["Codice SDI", viewBilling.billing_info.codice_sdi],
                  ["PEC", viewBilling.billing_info.pec],
                  ["Email fattura", viewBilling.billing_info.email_fattura],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-slate-100 py-1">
                    <span className="text-slate-500">{k}</span>
                    <span className="font-medium text-right select-all">{v}</span>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog testimonianza azienda */}
        <Dialog open={!!viewTestimonials} onOpenChange={(open) => !open && setViewTestimonials(null)}>
          <DialogContent className="max-w-lg" data-testid="testimonial-dialog">
            <DialogHeader>
              <DialogTitle>Testimonianza — {viewTestimonials?.company?.nome_azienda}</DialogTitle>
              <DialogDescription>Risposte alla card "Ci aiuti a crescere?"</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {(viewTestimonials?.items || []).map((t) => (
                <div key={t.id} className="border border-slate-200 rounded-lg p-4">
                  <p className="text-slate-800 italic">"{t.testo}"</p>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span>{t.user_nome} ({t.ruolo})</span>
                    <span>{t.user_email}</span>
                    <span>{formatDate(t.created_at)}</span>
                    {t.autorizza_pubblicazione ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">✅ Autorizzata alla pubblicazione</Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">❌ Non autorizzata</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        <RicevutaDialog token={token} company={ricevutaFor} onClose={() => setRicevutaFor(null)} onCreated={() => setRicevuteKey((k) => k + 1)} />
        <RicevuteDaEmettere companies={companies} stato={ricevuteStato} onEmetti={setRicevutaFor} />
        <RicevuteList token={token} refreshKey={ricevuteKey} />
        <SuperAdminRecensioni token={token} />

        {/* Testimonianze ricevute */}
        <Card className="mt-8" data-testid="super-admin-testimonials-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Testimonianze
            </CardTitle>
            <CardDescription>
              Risposte alla card "Ci aiuti a crescere?" — le più recenti in alto
            </CardDescription>
          </CardHeader>
          <CardContent>
            {testimonials.length === 0 ? (
              <div className="text-center py-8 text-slate-500" data-testid="no-testimonials">
                Nessuna testimonianza ricevuta finora
              </div>
            ) : (
              <div className="space-y-4">
                {testimonials.map((t) => (
                  <div key={t.id} className="border border-slate-200 rounded-lg p-4" data-testid="testimonial-item">
                    <p className="text-slate-800 italic">"{t.testo}"</p>
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                      <span className="font-medium text-slate-700">{t.nome_azienda || "Azienda sconosciuta"}</span>
                      <span>{t.user_nome} ({t.ruolo})</span>
                      <span>{t.user_email}</span>
                      <span>{formatDate(t.created_at)}</span>
                      {t.autorizza_pubblicazione ? (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">✅ Autorizzata alla pubblicazione</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">❌ Non autorizzata</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Box */}
        <div className="mt-8 p-4 bg-slate-100 rounded-lg text-sm text-slate-600">
          <p className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <strong>Nota:</strong> I dati vengono aggiornati in tempo reale. Le email di notifica vengono inviate automaticamente a <strong>support@ferie-e-permessi.it</strong> per ogni nuova registrazione.
          </p>
        </div>
      </main>
    </div>
  );
}
